/**
 * SeoRedirectHandler Component
 * 
 * This component handles redirects from old URL patterns to new SEO URLs.
 * It automatically converts old URLs to the new /ls/ or /pd/ format.
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFacetMeta } from '../../hooks/useFacetMeta';
import { URL_TYPES } from '../../constants/seoUrlPatterns';

const SeoRedirectHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { facetMappings, mappingsReady, fetchData } = useFacetMeta();

  useEffect(() => {
    const redirectToSeoUrl = async () => {
      // Ensure facet mappings are loaded
      if (!mappingsReady) {
        try {
          await fetchData();
        } catch (error) {
          console.error('Failed to fetch facet mappings for redirect:', error);
          // Fallback to default redirect
          navigate('/ls/handgun-ammo', { replace: true });
          return;
        }
      }

      const pathSegments = location.pathname.split('/').filter(Boolean);
      const searchParams = new URLSearchParams(location.search);
      
      // Handle different old URL patterns
      let newUrl = null;

      // 1. Handle old product URLs: /product/{id}
      if (pathSegments[0] === 'product' && pathSegments[1]) {
        // Default to products view for product URLs
        newUrl = `/pd/handgun-ammo`;
      }
      
      // 2. Handle old category URLs: /{category}
      else if (pathSegments.length === 1) {
        const category = pathSegments[0];
        
        // Map old category names to new weapon categories
        const categoryMap = {
          'handgun-ammo': 'handgun-ammo',
          'rifle-ammo': 'rifle-ammo',
          'rimfire-ammo': 'rimfire-ammo',
          'shotgun-ammo': 'shotgun-ammo',
          'handgun': 'handgun-ammo',
          'rifle': 'rifle-ammo',
          'rimfire': 'rimfire-ammo',
          'shotgun': 'shotgun-ammo'
        };
        
        const mappedCategory = categoryMap[category] || 'handgun-ammo';
        newUrl = `/ls/${mappedCategory}`;
      }
      
      // 3. Handle old category/caliber URLs: /{category}/{caliber}
      else if (pathSegments.length === 2) {
        const [category, caliber] = pathSegments;
        
        // Map category
        const categoryMap = {
          'handgun-ammo': 'handgun-ammo',
          'rifle-ammo': 'rifle-ammo',
          'rimfire-ammo': 'rimfire-ammo',
          'shotgun-ammo': 'shotgun-ammo',
          'handgun': 'handgun-ammo',
          'rifle': 'rifle-ammo',
          'rimfire': 'rimfire-ammo',
          'shotgun': 'shotgun-ammo'
        };
        
        const mappedCategory = categoryMap[category] || 'handgun-ammo';
        
        // Try to find caliber slug in mappings
        let caliberSlug = null;
        if (facetMappings?.caliber_label_reverse) {
          // Check if caliber is already a slug
          if (facetMappings.caliber_label_reverse[caliber]) {
            caliberSlug = caliber;
          } else {
            // Try to find by label
            for (const [label, slug] of Object.entries(facetMappings.caliber_label || {})) {
              if (label.toLowerCase() === caliber.toLowerCase()) {
                caliberSlug = slug;
                break;
              }
            }
          }
        }
        
        // Fallback to sanitized caliber name
        if (!caliberSlug) {
          caliberSlug = caliber.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        }
        
        newUrl = `/pd/${mappedCategory}/${caliberSlug}`;
      }
      
      // 4. Handle old brand URLs: /{brand}
      else if (pathSegments.length === 1 && isLikelyBrand(pathSegments[0])) {
        const brandName = pathSegments[0];
        
        // Try to find brand slug in mappings
        let brandSlug = null;
        if (facetMappings?.brand_label_reverse) {
          if (facetMappings.brand_label_reverse[brandName]) {
            brandSlug = brandName;
          } else {
            // Try to find by label
            for (const [label, slug] of Object.entries(facetMappings.brand_label || {})) {
              if (label.toLowerCase() === brandName.toLowerCase()) {
                brandSlug = slug;
                break;
              }
            }
          }
        }
        
        // Fallback to sanitized brand name
        if (!brandSlug) {
          brandSlug = brandName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        }
        
        newUrl = `/brands/${brandSlug}`;
      }
      
      // 5. Default fallback
      else {
        newUrl = `/ls/handgun-ammo`;
      }
      
      // Preserve query parameters
      if (searchParams.toString()) {
        newUrl += `?${searchParams.toString()}`;
      }
      
      // Redirect to new URL
      navigate(newUrl, { replace: true });
    };

    redirectToSeoUrl();
  }, [location.pathname, location.search, navigate, facetMappings, mappingsReady, fetchData]);

  // Show loading while redirecting
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontSize: '18px',
      color: '#666'
    }}>
      Redirecting to new URL...
    </div>
  );
};

// Helper function to determine if a slug is likely a brand
const isLikelyBrand = (slug) => {
  // Common brand patterns
  const brandPatterns = [
    /^[A-Z][a-z]+$/, // Capitalized single word
    /^[A-Z][a-z]+\-[A-Z][a-z]+$/, // Capitalized hyphenated
    /^aguila$/i, // Specific known brands
    /^federal$/i,
    /^winchester$/i,
    /^remington$/i
  ];
  
  return brandPatterns.some(pattern => pattern.test(slug));
};

export default SeoRedirectHandler;
